import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Phone, Mail, MapPin } from 'lucide-react';

export const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    bedrooms: '',
    bathrooms: '',
    stairs: '',
    squareFootage: '',
    pets: '',
    petType: '',
    serviceType: '',
    addOns: '',
    preferredDate: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Quote request submitted! We\'ll respond within 24 hours.');
        setFormData({
      fullName: '',
      phone: '',
      email: '',
      bedrooms: '',
      bathrooms: '',
      stairs: '',
      squareFootage: '',
      pets: '',
      petType: '',
      serviceType: '',
      addOns: '',
      preferredDate: '',
      notes: ''
        });
      } else {
        toast.error(result.message || 'Failed to submit quote request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit quote request. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-[#0B7CB5]/5 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get Your Free <span className="text-[#0B7CB5]">Cleaning Quote</span>
            </h2>
            <p className="text-xl text-gray-600">
              Fill out the form below for accurate, honest pricing. We'll respond within 24 hours.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Info</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Phone className="w-6 h-6 text-[#0B7CB5] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Phone</p>
                      <a href="tel:+18045837000" className="text-[#0B7CB5] hover:underline">
                        (804) 583-7000
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Mail className="w-6 h-6 text-[#0B7CB5] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Email</p>
                      <a href="mailto:custommaidsrva@gmail.com" className="text-[#0B7CB5] hover:underline break-all">
                        custommaidsrva@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-6 h-6 text-[#0B7CB5] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Service Area</p>
                      <p className="text-gray-600">Greater Richmond, VA</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#0B7CB5] p-6 rounded-xl text-white">
                <h4 className="text-xl font-bold mb-3">Quick Note</h4>
                <p className="text-white/90 leading-relaxed">
                  Recurring pricing is determined after your initial deep cleaning, as every home has different needs.
                </p>
              </div>
            </div>

            {/* Quote Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white p-8 rounded-xl shadow-lg space-y-6">
                {/* Contact Info Section */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#0B7CB5]/20">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Home Details Section */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#0B7CB5]/20">
                    Home Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="bedrooms">Number of Bedrooms *</Label>
                      <Input
                        id="bedrooms"
                        name="bedrooms"
                        type="number"
                        min="0"
                        value={formData.bedrooms}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bathrooms">Number of Bathrooms *</Label>
                      <Input
                        id="bathrooms"
                        name="bathrooms"
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.bathrooms}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="stairs">Stairs? *</Label>
                      <Select
                        value={formData.stairs}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, stairs: value }))}
                        required
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="squareFootage">Approx. Square Footage</Label>
                      <Input
                        id="squareFootage"
                        name="squareFootage"
                        type="number"
                        min="0"
                        value={formData.squareFootage}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pets">Pets in the Home? *</Label>
                      <Select
                        value={formData.pets}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, pets: value }))}
                        required
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.pets === 'yes' && (
                      <div>
                        <Label htmlFor="petType">Type of Pet(s)</Label>
                        <Input
                          id="petType"
                          name="petType"
                          value={formData.petType}
                          onChange={handleChange}
                          className="mt-1"
                          placeholder="e.g., Dog, Cat"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Service Details Section */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#0B7CB5]/20">
                    Service Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="serviceType">Type of Cleaning *</Label>
                      <Select
                        value={formData.serviceType}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, serviceType: value }))}
                        required
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select service type..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standard">Standard Cleaning</SelectItem>
                          <SelectItem value="deep">Deep / Initial Cleaning</SelectItem>
                          <SelectItem value="moveout">Move-Out Cleaning</SelectItem>
                          <SelectItem value="commercial">Commercial Cleaning</SelectItem>
                          <SelectItem value="airbnb">Airbnb Turnover</SelectItem>
                          <SelectItem value="construction">Post-Construction</SelectItem>
                          <SelectItem value="hazardous">Hazardous/Hoarder/Bio-Risk</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="addOns">Add-Ons Requested</Label>
                      <Input
                        id="addOns"
                        name="addOns"
                        value={formData.addOns}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="e.g., Baseboards, Inside Fridge, Inside Oven"
                      />
                    </div>
                    <div>
                      <Label htmlFor="preferredDate">Preferred Date/Time</Label>
                      <Input
                        id="preferredDate"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="e.g., Next Wednesday, Morning"
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes">Any Notes We Should Know?</Label>
                      <Textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="mt-1"
                        rows={4}
                        placeholder="Special instructions, hazardous conditions, access information, etc."
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-[#0B7CB5] hover:bg-[#095a87] text-white text-lg py-6 transition-all duration-200 hover:shadow-xl"
                >
                  Request Free Quote
                </Button>

                <p className="text-sm text-gray-600 text-center">
                  We'll respond within 24 hours with a customized quote. Recurring pricing is determined after your initial deep cleaning.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};